import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import useCRUD from "../hooks/useCRUD";
import { formatDateWithDay } from "../utils/dateUtils";
import Spinner from "../components/Spinner/Spinner";
import PassengerDetail from "../components/Driver/PassengerDetail";
import PickupGuideModal from "../components/Driver/PickupGuideModal";
import TimeModal from "../components/Elements/TimeModal";
import ParticipantsMapModal from "../components/EventDashboard/ParticipantsMapModal";
import OptimizeRouteModal from "../components/Driver/OptimizeRouteModal";
import DriverShuttlePage from "./DriverShuttlePage";
import RichTextContent from "../components/Elements/RichTextContent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMapLocationDot,
  faRoute,
  faBullhorn,
  faCalendarDays,
  faCircleCheck,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";

const DriverAssignmentList = () => {
  const { uniqueCode } = useParams();

  const [showTimeModal, setShowTimeModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [showOptimizeModal, setShowOptimizeModal] = useState(false);
  const [showPickupGuide, setShowPickupGuide] = useState(false);
  const [dismissedPickupGuideFor, setDismissedPickupGuideFor] = useState(null);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const {
    data: rideList,
    fetchItems: fetchRideList,
    isLoading,
    error,
  } = useCRUD(`/api/event-participations/assigned-driver-list/${uniqueCode}`);

  const { updateItem: updateConfirmation } = useCRUD(
    `/api/event-participations/confirm-ride`,
  );

  const { updateItem: updatePickup } = useCRUD(
    `/api/event-participations/update-sms-status`,
  );
  const { updateItem: markPickupGuideViewed } = useCRUD(
    `/api/event-participations/pickup-page-onboarding`,
  );
  const [editingPickup, setEditingPickup] = useState(null);

  const onSetTime = async (time) => {
    const updatePayload = { pickupTime: time };
    const response = await updatePickup(editingPickup, updatePayload);
    if (response) {
      alert("Pickup time updated and notification sent to passenger");
      fetchRideList();
      setShowTimeModal(false);
      setEditingPickup(null);
    }
  };

  const quickSetStatus = async (passengerId, status) => {
    const response = await updatePickup(passengerId, { pickupStatus: status });
    if (response) {
      fetchRideList();
    }
  };

  //Function to open time modal
  const openTimeModal = (pickupId) => {
    setEditingPickup(pickupId);
    console.log("pickupId", pickupId);
    setShowTimeModal(true);
  };

  const handleSendConfirmation = async () => {
    if (isAcknowledging) return;

    const updatePayload = { confirmedPickupAssignment: true };
    setIsAcknowledging(true);

    try {
      const response = await updateConfirmation(
        rideList.driverDetails.uniqueCode,
        updatePayload,
      );
      if (response) {
        alert("Notification sent");
        fetchRideList();
      }
    } finally {
      setIsAcknowledging(false);
    }
  };

  useEffect(() => {
    fetchRideList();
  }, [fetchRideList]);

  const driverParticipationId = rideList?.driverDetails?.id;

  useEffect(() => {
    if (
      rideList?.driverDetails?.showPickupPageOnboarding &&
      !rideList?.driverDetails?.pickupPageOnboardingViewedAt &&
      driverParticipationId &&
      dismissedPickupGuideFor !== driverParticipationId
    ) {
      setShowPickupGuide(true);
    }
  }, [
    dismissedPickupGuideFor,
    driverParticipationId,
    rideList?.driverDetails?.pickupPageOnboardingViewedAt,
    rideList?.driverDetails?.showPickupPageOnboarding,
  ]);

  if (isLoading) return <Spinner />;
  if (error)
    return <div className="text-center text-red-500">Invalid Request</div>;
  if (!rideList)
    return <div className="text-center text-red-500">No Rides Found</div>;
  if (!rideList.isDriver)
    return <div className="text-center text-red-500">Unauthorized</div>;

  if (rideList.eventDetails?.eventSignUpType === "Shuttle Event") {
    return <DriverShuttlePage uniqueCode={uniqueCode} />;
  }

  const {
    driverDetails,
    passengers,
    eventDetails,
    messageFromAdmin,
    accountLogo,
  } = rideList;

  const closePickupGuide = async () => {
    setDismissedPickupGuideFor(driverDetails.id);
    setShowPickupGuide(false);

    if (!driverDetails.pickupPageOnboardingViewedAt) {
      const response = await markPickupGuideViewed(
        driverDetails.uniqueCode,
        {},
      );

      if (response) {
        fetchRideList();
      }
    }
  };

  const sortedPassengers = [...passengers].sort((a, b) => {
    // Check if both passengers have a pickup time
    if (a.pickupTime && b.pickupTime) {
      // Convert time to a comparable number (minutes from start of the day)
      const getTimeInMinutes = (time) => {
        const [hour, minutePart] = time.split(":");
        const minutes = parseInt(minutePart.split(" ")[0], 10);
        const isPM = minutePart.split(" ")[1] === "PM";
        return (
          parseInt(hour, 10) * 60 +
          minutes +
          (isPM && hour !== "12" ? 12 * 60 : 0) -
          (hour === "12" && !isPM ? 12 * 60 : 0)
        );
      };

      const aTimeInMinutes = getTimeInMinutes(a.pickupTime);
      const bTimeInMinutes = getTimeInMinutes(b.pickupTime);

      return aTimeInMinutes - bTimeInMinutes;
    }

    // If only one of the passengers has a pickup time, prioritize it
    if (a.pickupTime) return -1;
    if (b.pickupTime) return 1;

    // If neither has a pickup time, sort by rideLocation
    return a.rideLocation.localeCompare(b.rideLocation);
  });

  const currentDate = new Date();
  const eventDate = new Date(eventDetails.dateOfOccurrence);

  // Add 12 hours to eventDate in UTC
  eventDate.setUTCHours(eventDate.getUTCHours() + 12);

  // If you want to compare the current date in UTC as well
  // Convert currentDate to UTC by creating a new Date object in UTC
  const currentUTCDate = new Date(
    Date.UTC(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate(),
      currentDate.getHours(),
      currentDate.getMinutes(),
      currentDate.getSeconds(),
    ),
  );

  const passengersWithCoords = sortedPassengers.filter(
    (p) =>
      p.pickupLatitude !== null &&
      p.pickupLatitude !== undefined &&
      p.pickupLongitude !== null &&
      p.pickupLongitude !== undefined,
  );

  const isEventExpired = eventDate < currentUTCDate;

  return (
    <div
      className="min-h-screen bg-no-repeat bg-cover bg-center py-6 px-3 sm:py-10"
      style={{ backgroundImage: "url('/background.png')" }}
    >
      <div className="max-w-3xl mx-auto space-y-4">
        {/* Header card: logo + greeting */}
        <div className="bg-white/95 rounded-3xl shadow-xl p-5 sm:p-6 border border-white/60">
          {accountLogo && (
            <div className="flex justify-center mb-4">
              <img
                src={accountLogo}
                alt="Account Logo"
                className="object-contain"
                style={{ maxWidth: "120px", maxHeight: "120px" }}
              />
            </div>
          )}
          <div className="text-center sm:text-left sm:flex sm:items-end sm:justify-between sm:gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Hi, {driverDetails.name}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {isEventExpired
                  ? "This event has ended."
                  : sortedPassengers.length > 0
                    ? "Here's your pickup schedule for today."
                    : "Thanks for volunteering to drive."}
              </p>
            </div>
          </div>
        </div>

        {isEventExpired ? (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center text-red-500 font-medium">
            Event expired.
          </div>
        ) : (
          <>
            {/* Event details card */}
            <div className="relative bg-white rounded-2xl shadow-lg overflow-visible border border-gray-100">
              <div className="p-5 sm:p-6 border-b border-gray-100">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                  {eventDetails.title}
                </h2>
                <div className="mt-3 space-y-2 text-sm text-gray-600">
                  <p className="flex items-start gap-2">
                    <FontAwesomeIcon
                      icon={faCalendarDays}
                      className="mt-0.5 text-[#004a7e]"
                    />
                    <span>
                      {formatDateWithDay(eventDetails.dateOfOccurrence)}
                    </span>
                  </p>
                  {eventDetails.location && (
                    <p className="flex items-start gap-2">
                      <FontAwesomeIcon
                        icon={faLocationDot}
                        className="mt-0.5 text-[#004a7e]"
                      />
                      <span>{eventDetails.location}</span>
                    </p>
                  )}
                </div>
              </div>

              <div className="p-5 sm:p-6">
                {sortedPassengers.length > 0 ? (
                  <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
                    <div className="rounded-xl bg-gray-50 p-3 border border-gray-100">
                      <p className="font-semibold text-gray-900">
                        Manage each rider from their pickup card.
                      </p>
                      <ul className="mt-2 list-disc pl-5 space-y-1">
                        <li>
                          Use the pickup time button to set or edit a time.
                        </li>
                        <li>Use the status steps to send rider updates.</li>
                        <li>
                          Pickup time and status updates notify the passenger.
                        </li>
                      </ul>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    You don&apos;t have any pickups assigned yet.
                  </p>
                )}
              </div>
            </div>

            {eventDetails.eventMessageForDrivers && (
              <div className="bg-amber-50 border border-amber-300 border-l-4 border-l-amber-500 rounded-2xl p-4 flex gap-3 items-start shadow-lg">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center">
                  <FontAwesomeIcon icon={faBullhorn} />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-sm font-semibold text-amber-900 uppercase tracking-wide">
                    Event Notice
                  </h2>
                  <RichTextContent
                    html={eventDetails.eventMessageForDrivers}
                    className="mt-1 text-amber-950"
                  />
                </div>
              </div>
            )}

            {showTimeModal && (
              <TimeModal
                isOpen={showTimeModal}
                pickupId={editingPickup}
                onClose={() => setShowTimeModal(false)}
                onSave={(onTime) => onSetTime(onTime)}
              />
            )}
            {showMapModal && (
              <ParticipantsMapModal
                isOpen={showMapModal}
                onClose={() => setShowMapModal(false)}
                participants={passengersWithCoords}
              />
            )}
            {showOptimizeModal && (
              <OptimizeRouteModal
                isOpen={showOptimizeModal}
                onClose={() => setShowOptimizeModal(false)}
                uniqueCode={uniqueCode}
                onApplied={fetchRideList}
              />
            )}
            {showPickupGuide && (
              <PickupGuideModal
                isOpen={showPickupGuide}
                onClose={closePickupGuide}
              />
            )}

            {/* Pickup list */}
            {sortedPassengers.length > 0 && (
              <div className="relative bg-white rounded-2xl shadow-lg overflow-visible">
                <div className="absolute -left-2 top-4 z-10 bg-[#004a7e] px-4 py-1.5 text-xs font-black uppercase tracking-wide text-white shadow-md">
                  {sortedPassengers.length}{" "}
                  {sortedPassengers.length === 1 ? "Pickup" : "Pickups"}
                </div>
                <div className="px-5 py-4 border-b border-gray-100 space-y-3 sm:space-y-0 sm:flex sm:items-center sm:justify-between sm:gap-3">
                  <div className="h-7" aria-hidden="true" />
                  {passengersWithCoords.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
                      <button
                        data-tour="view-map"
                        className="flex items-center justify-center gap-2 rounded-xl bg-[#004a7e] px-3 py-2 text-xs font-bold text-white shadow-sm hover:bg-[#003a64] transition-colors"
                        onClick={() => setShowMapModal(true)}
                        title={`Plot ${passengersWithCoords.length} pickup(s) on a map`}
                      >
                        <FontAwesomeIcon icon={faMapLocationDot} />
                        View Map
                      </button>
                      <button
                        data-tour="optimize-route"
                        className="flex items-center justify-center gap-2 rounded-xl bg-success px-3 py-2 text-xs font-bold text-white shadow-sm hover:bg-green-700 transition-colors"
                        onClick={() => setShowOptimizeModal(true)}
                        title="Calculate the most efficient pickup order and times"
                      >
                        <FontAwesomeIcon icon={faRoute} />
                        Optimize
                      </button>
                    </div>
                  )}
                </div>
                <ol className="divide-y divide-gray-100">
                  {sortedPassengers.map((passenger, index) => (
                    <PassengerDetail
                      passenger={passenger}
                      key={passenger.id || passenger._id}
                      index={index + 1}
                      openTimeModal={openTimeModal}
                      quickSetStatus={quickSetStatus}
                      confirmedPickupAssignment={
                        driverDetails.confirmedPickupAssignment
                      }
                    />
                  ))}
                </ol>
              </div>
            )}

            {messageFromAdmin && (
              <div className="bg-orange-50 border border-orange-300 border-l-4 border-l-orange-500 rounded-2xl p-4 flex gap-3 items-start shadow-lg">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center">
                  <FontAwesomeIcon icon={faBullhorn} />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-sm font-semibold text-orange-900 uppercase tracking-wide">
                    Message from Admin
                  </h2>
                  <p className="text-orange-950 mt-1 text-sm leading-relaxed">
                    {messageFromAdmin}
                  </p>
                </div>
              </div>
            )}

            {!driverDetails.confirmedPickupAssignment &&
              sortedPassengers.length > 0 && (
                <div className="sticky bottom-3 z-10">
                  <button
                    className="w-full bg-[#004a7e] hover:bg-[#003a64] disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-2xl shadow-lg flex items-center justify-center gap-2 transition-colors"
                    onClick={handleSendConfirmation}
                    disabled={isAcknowledging}
                    title="Click here to let the admin know that you have received the pickup list"
                  >
                    {isAcknowledging ? (
                      <span className="loading loading-spinner loading-sm" />
                    ) : (
                      <FontAwesomeIcon icon={faCircleCheck} />
                    )}
                    {isAcknowledging
                      ? "Acknowledging..."
                      : "Acknowledge Assignments"}
                  </button>
                </div>
              )}
          </>
        )}
      </div>
    </div>
  );
};

export default DriverAssignmentList;
